import { Drawer, type PaperProps } from "@mui/material";
import { type ReactNode } from "react";
import { useResizableWidth } from "../../hooks/use-resizable-width";
import { ResizeHandle } from "./ResizeHandle";

interface ResizableDrawerProps {
	open: boolean;
	onClose: () => void;
	children: ReactNode;
	/** Initial width as % of viewport. */
	defaultWidthPct?: number;
	/** Drag clamp bounds (% of viewport). */
	minWidthPct?: number;
	maxWidthPct?: number;
	/** data-component label for the drawer paper. */
	dataComponent?: string;
	/** Raise above the modal layer — use when opened from inside a Dialog. */
	aboveModals?: boolean;
	/** What the panel shows, for screen readers and the assistant ("Role"). */
	label?: string;
	id?: string;
}

/**
 * Right-anchored drawer whose width is set by dragging its left edge. The 8px
 * handle highlights in the primary colour on hover. Shared by every large
 * right-side detail panel (person, assignment, CV compare).
 *
 * It is modal (a backdrop, Escape closes it), so its panel is a dialog, as
 * WAI-ARIA's modal dialog pattern says: a screen reader announces it, and the
 * assistant's page reader and the screen-map crawl read it as what a button
 * opened.
 */
export const ResizableDrawer = ({
	open,
	onClose,
	children,
	defaultWidthPct = 80,
	minWidthPct = 20,
	maxWidthPct = 90,
	dataComponent = "resizable-drawer",
	aboveModals = false,
	label,
	id,
}: ResizableDrawerProps) => {
	const { widthPct, startResize } = useResizableWidth(
		defaultWidthPct,
		minWidthPct,
		maxWidthPct,
		`drawer-width:${dataComponent}`
	);
	return (
		<Drawer
			id={id}
			open={open}
			onClose={onClose}
			anchor="right"
			sx={aboveModals ? { zIndex: (theme) => theme.zIndex.modal + 1 } : undefined}
			slotProps={{
				paper: {
					"data-component": dataComponent,
					role: "dialog",
					"aria-modal": true,
					"aria-label": label,
					sx: {
						width: `${widthPct}%`,
						background: (theme) => theme.palette.background.default,
					},
				} as PaperProps & { "data-component": string },
			}}
		>
			{/* Drag the left edge to resize the drawer. */}
			<ResizeHandle
				onPointerDown={startResize}
				cursor="ew-resize"
				absolute
				dataComponent="drawer-resize-handle"
			/>
			{children}
		</Drawer>
	);
};
